import "@cva/renderer/fonts.css";
import { SceneRenderer } from "@cva/renderer";
import { APP_NAME, pageSize, type ShareView, setCustomFonts } from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Film, Link2Off, Loader2, MapPin, MessageSquare, Presentation } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  buildThreads,
  CommentComposer,
  CommentPin,
  CommentThreadView,
  threadLabel,
} from "../components/collab";
import { Logo, Mark } from "../components/env-badge";
import { LegalLinks } from "../components/legal-links";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Presentation as PresentationView } from "../editor/Presentation";
import { client, orpc } from "../lib/api";
import { cn, errorMessage, formatDate, formatRelative } from "../lib/utils";

/**
 * Public page of a share link (docs/15-zusammenarbeit.md): the file without an
 * account. Designs are drawn by the same renderer as the editor, videos play
 * their latest MP4 export. Links that allow it let guests comment (pins on the
 * page or at a moment of the video).
 */

const NAME_KEY = "kaleido-guest-name";

function storedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberName(name: string) {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private mode: the name is just asked again next time.
  }
}

/** Organization fonts of the file, as FontFace objects (URLs carry the token). */
function useShareFonts(fonts: ShareView["fonts"] | undefined) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!fonts) return;
    const faces: globalThis.FontFace[] = [];
    for (const font of fonts) {
      for (const face of font.faces) {
        const fontFace = new FontFace(font.family, `url("${face.url}")`, {
          weight: String(face.weight),
          style: face.style,
          display: "swap",
          ...(face.unicodeRange ? { unicodeRange: face.unicodeRange } : {}),
        });
        document.fonts.add(fontFace);
        faces.push(fontFace);
      }
    }
    setCustomFonts(
      fonts.map((font) => ({
        family: font.family,
        category: "custom",
        weights: [...new Set(font.faces.map((face) => face.weight))].sort((a, b) => a - b),
        italics: [],
      })),
    );
    void Promise.allSettled(faces.map((face) => face.load())).then(() => setReady(true));
    return () => {
      for (const face of faces) document.fonts.delete(face);
    };
  }, [fonts]);
  return ready || fonts?.length === 0;
}

/** Keeps search engines away from shared files. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

interface Draft {
  pageId: string;
  x: number;
  y: number;
}

/**
 * Changes to the file and new comments show up within seconds: the server
 * says "changed" and the page loads the file again (docs/17-live-kollaboration.md).
 */
function useLiveRefresh(token: string) {
  const queryClient = useQueryClient();
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const source = new EventSource(`/api/live/share?${new URLSearchParams({ token })}`);
    source.onmessage = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: orpc.share.view.key({ input: { token } }) });
      }, 400);
    };
    return () => {
      if (timer) clearTimeout(timer);
      source.close();
    };
  }, [token, queryClient]);
}

export function SharePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  useNoIndex();
  const view = useQuery({
    ...orpc.share.view.queryOptions({ input: { token } }),
    retry: false,
    refetchOnWindowFocus: false,
    // Fallback for when the live refresh below cannot connect.
    refetchInterval: 60_000,
  });
  useLiveRefresh(token);
  if (view.isError) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-canvas p-6 text-center">
        <Logo />
        <div className="max-w-sm rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
          <Link2Off className="mx-auto mb-3 size-8 text-zinc-300" />
          <p className="font-semibold text-zinc-900">Dieser Link funktioniert nicht (mehr)</p>
          <p className="mt-1 text-sm text-zinc-500">
            {errorMessage(
              view.error,
              "Er wurde widerrufen, ist abgelaufen oder die Datei wurde gelöscht.",
            )}{" "}
            Frag bei der Person nach, die ihn dir geschickt hat.
          </p>
        </div>
        <LegalLinks />
      </div>
    );
  }
  if (!view.data) {
    return (
      <div className="flex h-full items-center justify-center text-zinc-400">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }
  return <ShareContent token={token} view={view.data} />;
}

function ShareContent({ token, view }: { token: string; view: ShareView }) {
  const queryClient = useQueryClient();
  const fontsReady = useShareFonts(view.fonts);
  const [name, setName] = useState(storedName);
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [panel, setPanel] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [presenting, setPresenting] = useState(false);
  const canComment = view.access !== "view";
  const isVideo = view.kind === "video";
  const threads = useMemo(() => buildThreads(view.comments), [view.comments]);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: orpc.share.view.key({ input: { token } }) });
  const [contentRef, contentWidth] = useWidth<HTMLDivElement>();
  const resolveAsset = (assetId: string) => view.assets[assetId];

  useEffect(() => {
    document.title = `${view.title} – ${APP_NAME}`;
  }, [view.title]);

  const needName = () => {
    if (name.trim()) {
      rememberName(name.trim());
      return false;
    }
    toast.error("Bitte gib zuerst deinen Namen an");
    document.getElementById("guest-name")?.focus();
    return true;
  };
  const comment = useMutation({
    mutationFn: (input: {
      body: string;
      parentId?: string;
      pageId?: string;
      x?: number;
      y?: number;
      timeMs?: number;
    }) => client.share.comment({ token, name: name.trim(), ...input }),
    onSuccess: (created) => {
      void refresh();
      setActive(created.parentId ?? created.id);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const send = async (input: Parameters<typeof comment.mutateAsync>[0]) => {
    if (needName()) throw new Error("Name fehlt");
    return comment.mutateAsync(input);
  };

  // Every page fits on the screen (upright flyers too).
  const ratio = view.content.format.width / view.content.format.height;
  const pageWidth = Math.max(
    200,
    Math.min(contentWidth - 32, 1000, (window.innerHeight - 160) * ratio),
  );
  /** Pages may have their own size: each one is scaled to fit the screen on its own. */
  const scaleOf = (index: number) => {
    const size = pageSize(view.content, view.content.pages[index]);
    const width = Math.max(
      200,
      Math.min(contentWidth - 32, 1000, (window.innerHeight - 160) * (size.width / size.height)),
    );
    return width / size.width;
  };

  return (
    <div className="flex h-full flex-col bg-zinc-100">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-zinc-200 bg-white px-4">
        <Mark className="size-7" title={APP_NAME} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900">{view.title}</p>
          <p className="truncate text-[11px] text-zinc-500">
            {view.orgName} · geändert {formatRelative(view.updatedAt)}
            {view.expiresAt ? ` · Link gültig bis ${formatDate(view.expiresAt)}` : ""}
          </p>
        </div>
        {!isVideo ? (
          <Button size="sm" variant="secondary" onClick={() => setPresenting(true)}>
            <Presentation /> Präsentieren
          </Button>
        ) : null}
        {canComment ? (
          <>
            {!isVideo ? (
              <Button
                size="sm"
                variant={placing ? "primary" : "secondary"}
                onClick={() => {
                  setDraft(null);
                  setPlacing(!placing);
                  setPanel(true);
                }}
              >
                <MapPin /> {placing ? "Klicke auf die Stelle …" : "Kommentieren"}
              </Button>
            ) : null}
            <button
              type="button"
              onClick={() => setPanel(!panel)}
              title="Kommentare"
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-sm text-zinc-700 hover:bg-zinc-100 [&_svg]:size-4",
                panel && "bg-brand-50 text-brand-700",
              )}
            >
              <MessageSquare />
              {threads.filter((thread) => !thread.root.resolvedAt).length}
            </button>
          </>
        ) : null}
      </header>
      {presenting ? (
        <PresentationView
          document={view.content}
          resolveAsset={resolveAsset}
          title={view.title}
          onClose={() => setPresenting(false)}
        />
      ) : null}
      <div className="flex min-h-0 flex-1">
        <main ref={contentRef} className="min-w-0 flex-1 overflow-y-auto">
          <div className="flex flex-col items-center gap-8 px-4 py-8">
            {isVideo ? (
              <VideoView view={view} videoRef={videoRef} width={pageWidth} />
            ) : contentWidth > 0 ? (
              view.content.pages.map((page, index) => {
                const size = pageSize(view.content, page);
                const pageScale = scaleOf(index);
                return (
                  <section key={page.id} className="space-y-2">
                    {view.content.pages.length > 1 ? (
                      <p className="text-xs font-medium text-zinc-500">
                        Seite {index + 1} von {view.content.pages.length}
                      </p>
                    ) : null}
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: clicking the page places a comment pin */}
                    <div
                      className={cn("relative bg-white shadow-lg", placing && "cursor-crosshair")}
                      style={{
                        width: size.width * pageScale,
                        height: size.height * pageScale,
                      }}
                      onClick={(event) => {
                        if (!placing) return;
                        const rect = event.currentTarget.getBoundingClientRect();
                        setDraft({
                          pageId: page.id,
                          x: Math.round((event.clientX - rect.left) / pageScale),
                          y: Math.round((event.clientY - rect.top) / pageScale),
                        });
                        setPlacing(false);
                      }}
                    >
                      <div
                        style={{
                          transform: `scale(${pageScale})`,
                          transformOrigin: "0 0",
                          pointerEvents: "none",
                          opacity: fontsReady ? 1 : 0,
                          transition: "opacity 150ms",
                        }}
                      >
                        <SceneRenderer
                          document={view.content}
                          pageIndex={index}
                          resolveAsset={resolveAsset}
                        />
                      </div>
                      {canComment && panel ? (
                        <div
                          className="absolute top-0 left-0"
                          style={{ transform: `scale(${pageScale})`, transformOrigin: "0 0" }}
                        >
                          {threads
                            .filter(
                              (thread) =>
                                !thread.root.resolvedAt &&
                                thread.root.x !== null &&
                                (thread.root.pageId === page.id ||
                                  (!thread.root.pageId && index === 0)),
                            )
                            .map((thread) => (
                              <CommentPin
                                key={thread.root.id}
                                x={thread.root.x!}
                                y={thread.root.y!}
                                number={thread.number}
                                scale={pageScale}
                                active={active === thread.root.id}
                                onClick={() => {
                                  setActive(thread.root.id);
                                  document
                                    .querySelector(`[data-thread="${thread.root.id}"]`)
                                    ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                                }}
                              />
                            ))}
                          {draft?.pageId === page.id ? (
                            <CommentPin x={draft.x} y={draft.y} scale={pageScale} draft />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </section>
                );
              })
            ) : null}
          </div>
          <footer className="flex flex-col items-center gap-2 pb-8 text-xs text-zinc-500">
            <a href="/" className="inline-flex items-center gap-1.5 hover:text-zinc-900">
              Geteilt mit <Logo className="h-4 w-20" />
            </a>
            <LegalLinks />
          </footer>
        </main>
        {canComment && panel ? (
          <aside className="flex w-80 shrink-0 flex-col border-l border-zinc-200 bg-white">
            <div className="space-y-1.5 border-b border-zinc-100 p-3">
              <label htmlFor="guest-name" className="text-xs font-medium text-zinc-600">
                Dein Name (erscheint bei deinen Kommentaren)
              </label>
              <Input
                id="guest-name"
                value={name}
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => name.trim() && rememberName(name.trim())}
                placeholder="Vor- und Nachname"
                className="h-8 text-sm"
              />
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
              {draft ? (
                <div className="rounded-xl border border-brand-300 bg-brand-50/50 p-3">
                  <p className="mb-2 text-xs font-medium text-brand-800">
                    Kommentar an der markierten Stelle
                  </p>
                  <CommentComposer
                    autoFocus
                    onCancel={() => setDraft(null)}
                    onSubmit={async (body) => {
                      await send({ body, ...draft });
                      setDraft(null);
                    }}
                  />
                </div>
              ) : null}
              {threads.length === 0 && !draft ? (
                <p className="rounded-lg bg-zinc-50 p-4 text-center text-xs text-zinc-500">
                  {isVideo
                    ? "Noch keine Kommentare. Halte das Video an der Stelle an und schreib unten, was dir auffällt."
                    : "Noch keine Kommentare. Klick auf „Kommentieren“ und dann auf die Stelle, um die es geht."}
                </p>
              ) : (
                threads.map((thread) => (
                  <div key={thread.root.id} data-thread={thread.root.id}>
                    <CommentThreadView
                      thread={thread}
                      active={active === thread.root.id}
                      onActivate={() => {
                        setActive(thread.root.id);
                        if (isVideo && thread.root.timeMs !== null && videoRef.current) {
                          videoRef.current.currentTime = thread.root.timeMs / 1000;
                          videoRef.current.pause();
                        }
                      }}
                      label={threadLabel(thread.root, view.content.pages, isVideo)}
                      actions={{
                        reply: (parentId, body) => send({ parentId, body }),
                      }}
                    />
                  </div>
                ))
              )}
            </div>
            <div className="border-t border-zinc-100 p-3">
              <CommentComposer
                placeholder={
                  isVideo ? "Kommentar zur aktuellen Stelle im Video …" : "Allgemeiner Kommentar …"
                }
                onSubmit={(body) =>
                  send({
                    body,
                    ...(isVideo && videoRef.current
                      ? { timeMs: Math.round(videoRef.current.currentTime * 1000) }
                      : { pageId: view.content.pages[0]?.id }),
                  })
                }
              />
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function VideoView({
  view,
  videoRef,
  width,
}: {
  view: ShareView;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  width: number;
}) {
  const ratio = view.content.format.width / view.content.format.height;
  // Upright videos should fit on the screen.
  const height = Math.min(window.innerHeight * 0.72, width / ratio);
  if (!view.video) {
    return (
      <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl bg-white p-8 text-center shadow-sm">
        {view.thumbnailUrl ? (
          <img src={view.thumbnailUrl} alt="" className="max-h-64 rounded-lg shadow" />
        ) : (
          <Film className="size-10 text-zinc-300" />
        )}
        <p className="text-sm text-zinc-600">
          Von diesem Video gibt es noch keinen Export. Sobald es exportiert ist, lässt es sich hier
          abspielen.
        </p>
      </div>
    );
  }
  const outdated = view.video.revision < view.revision;
  return (
    <div className="flex flex-col items-center gap-2">
      <video
        ref={videoRef}
        src={view.video.url}
        controls
        playsInline
        preload="metadata"
        poster={view.thumbnailUrl ?? undefined}
        className="rounded-lg bg-black shadow-lg"
        style={{ height, width: height * ratio }}
      >
        <track kind="captions" />
      </video>
      <p className="text-xs text-zinc-500">
        Export vom {formatDate(view.video.createdAt)}
        {outdated ? " · die Datei wurde seitdem geändert" : ""}
      </p>
    </div>
  );
}
